"use client";

import { useState, useEffect } from "react";
import { Save, AlertCircle, CheckCircle } from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // General settings
  const [generalSettings, setGeneralSettings] = useState({
    siteName: "",
    siteDescription: "",
    siteUrl: "",
    adminEmail: "",
    postsPerPage: 10,
    allowComments: true,
    moderateComments: true,
    maintenanceMode: false,
  });

  // User settings
  const [userSettings, setUserSettings] = useState({
    allowRegistration: true,
    defaultUserRole: "subscriber",
    requireEmailVerification: true,
    loginAttempts: 5,
    lockoutTime: 30,
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    newUserNotification: true,
    newCommentNotification: true,
    contactFormNotification: true,
  });

  // API settings
  const [apiSettings, setApiSettings] = useState({
    enableApi: true,
    apiKey: "",
    allowCors: false,
    rateLimitRequests: 100,
    rateLimitPeriod: 60,
  });

  // Load mock settings
  useEffect(() => {
    const timer = setTimeout(() => {
      // Simulate API call to load settings
      setGeneralSettings({
        siteName: "Admin Dashboard",
        siteDescription: "A powerful admin dashboard for content management",
        siteUrl: "https://example.com",
        adminEmail: "admin@example.com",
        postsPerPage: 10,
        allowComments: true,
        moderateComments: true,
        maintenanceMode: false,
      });

      setUserSettings({
        allowRegistration: true,
        defaultUserRole: "subscriber",
        requireEmailVerification: true,
        loginAttempts: 5,
        lockoutTime: 30,
      });

      setNotificationSettings({
        emailNotifications: true,
        newUserNotification: true,
        newCommentNotification: true,
        contactFormNotification: true,
      });

      setApiSettings({
        enableApi: true,
        apiKey: "sk_test_12345abcdef",
        allowCors: false,
        rateLimitRequests: 100,
        rateLimitPeriod: 60,
      });

      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(false);

    // Simulate API call to save settings
    setTimeout(() => {
      setSaving(false);
      setSaveSuccess(true);

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 1500);
  };

  // Generate a new API key
  const generateApiKey = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "sk_test_";
    for (let i = 0; i < 16; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    setApiSettings({ ...apiSettings, apiKey: result });
  };

  if (loading) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure your application settings
          </p>
        </div>

        <div className="animate-pulse space-y-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="mb-6">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/5"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure your application settings
        </p>
      </div>

      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-green-800">
              Settings saved successfully
            </h3>
            <p className="mt-1 text-sm text-green-700">
              Your changes have been applied.
            </p>
          </div>
        </div>
      )}

      {saveError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">
              Error saving settings
            </h3>
            <p className="mt-1 text-sm text-red-700">
              There was a problem saving your changes. Please try again.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* General Settings */}
        <Card className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            General Settings
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Name
                </label>
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={generalSettings.siteName}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      siteName: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admin Email
                </label>
                <input
                  type="email"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={generalSettings.adminEmail}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      adminEmail: e.target.value,
                    })
                  }
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site Description
              </label>
              <textarea
                rows={2}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={generalSettings.siteDescription}
                onChange={(e) =>
                  setGeneralSettings({
                    ...generalSettings,
                    siteDescription: e.target.value,
                  })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site URL
              </label>
              <input
                type="url"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={generalSettings.siteUrl}
                onChange={(e) =>
                  setGeneralSettings({
                    ...generalSettings,
                    siteUrl: e.target.value,
                  })
                }
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Posts Per Page
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={generalSettings.postsPerPage}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      postsPerPage: Number.parseInt(e.target.value),
                    })
                  }
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="allowComments"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={generalSettings.allowComments}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      allowComments: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="allowComments"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Allow comments on posts
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="moderateComments"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={generalSettings.moderateComments}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      moderateComments: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="moderateComments"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Moderate comments before publishing
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="maintenanceMode"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={generalSettings.maintenanceMode}
                  onChange={(e) =>
                    setGeneralSettings({
                      ...generalSettings,
                      maintenanceMode: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="maintenanceMode"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Enable maintenance mode
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* User Settings */}
        <Card className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            User Settings
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default User Role
                </label>
                <select
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={userSettings.defaultUserRole}
                  onChange={(e) =>
                    setUserSettings({
                      ...userSettings,
                      defaultUserRole: e.target.value,
                    })
                  }
                >
                  <option value="subscriber">Subscriber</option>
                  <option value="contributor">Contributor</option>
                  <option value="author">Author</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Login Attempts
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={userSettings.loginAttempts}
                  onChange={(e) =>
                    setUserSettings({
                      ...userSettings,
                      loginAttempts: Number.parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lockout Time (minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={userSettings.lockoutTime}
                  onChange={(e) =>
                    setUserSettings({
                      ...userSettings,
                      lockoutTime: Number.parseInt(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="allowRegistration"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={userSettings.allowRegistration}
                  onChange={(e) =>
                    setUserSettings({
                      ...userSettings,
                      allowRegistration: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="allowRegistration"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Allow user registration
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="requireEmailVerification"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={userSettings.requireEmailVerification}
                  onChange={(e) =>
                    setUserSettings({
                      ...userSettings,
                      requireEmailVerification: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="requireEmailVerification"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Require email verification
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Notification Settings */}
        <Card className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Notification Settings
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  id="emailNotifications"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={notificationSettings.emailNotifications}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: e.target.checked,
                    })
                  }
                />
                <label
                  htmlFor="emailNotifications"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Enable email notifications
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="newUserNotification"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={notificationSettings.newUserNotification}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      newUserNotification: e.target.checked,
                    })
                  }
                  disabled={!notificationSettings.emailNotifications}
                />
                <label
                  htmlFor="newUserNotification"
                  className={`ml-2 block text-sm ${
                    notificationSettings.emailNotifications
                      ? "text-gray-900"
                      : "text-gray-400"
                  }`}
                >
                  Notify admin when new user registers
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="newCommentNotification"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={notificationSettings.newCommentNotification}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      newCommentNotification: e.target.checked,
                    })
                  }
                  disabled={!notificationSettings.emailNotifications}
                />
                <label
                  htmlFor="newCommentNotification"
                  className={`ml-2 block text-sm ${
                    notificationSettings.emailNotifications
                      ? "text-gray-900"
                      : "text-gray-400"
                  }`}
                >
                  Notify admin when new comment is posted
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="contactFormNotification"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={notificationSettings.contactFormNotification}
                  onChange={(e) =>
                    setNotificationSettings({
                      ...notificationSettings,
                      contactFormNotification: e.target.checked,
                    })
                  }
                  disabled={!notificationSettings.emailNotifications}
                />
                <label
                  htmlFor="contactFormNotification"
                  className={`ml-2 block text-sm ${
                    notificationSettings.emailNotifications
                      ? "text-gray-900"
                      : "text-gray-400"
                  }`}
                >
                  Notify admin when contact form is submitted
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* API Settings */}
        <Card className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            API Settings
          </h2>
          <div className="space-y-4">
            <div className="flex items-center mb-4">
              <input
                id="enableApi"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={apiSettings.enableApi}
                onChange={(e) =>
                  setApiSettings({
                    ...apiSettings,
                    enableApi: e.target.checked,
                  })
                }
              />
              <label
                htmlFor="enableApi"
                className="ml-2 block text-sm text-gray-900"
              >
                Enable API access
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key
              </label>
              <div className="flex">
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-l-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={apiSettings.apiKey}
                  readOnly
                  disabled={!apiSettings.enableApi}
                />
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-r-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={generateApiKey}
                  disabled={!apiSettings.enableApi}
                >
                  Generate
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Keep this key secret. You can regenerate it at any time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate Limit (requests)
                </label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={apiSettings.rateLimitRequests}
                  onChange={(e) =>
                    setApiSettings({
                      ...apiSettings,
                      rateLimitRequests: Number.parseInt(e.target.value),
                    })
                  }
                  disabled={!apiSettings.enableApi}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate Limit Period (seconds)
                </label>
                <input
                  type="number"
                  min="10"
                  max="3600"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={apiSettings.rateLimitPeriod}
                  onChange={(e) =>
                    setApiSettings({
                      ...apiSettings,
                      rateLimitPeriod: Number.parseInt(e.target.value),
                    })
                  }
                  disabled={!apiSettings.enableApi}
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="allowCors"
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={apiSettings.allowCors}
                onChange={(e) =>
                  setApiSettings({
                    ...apiSettings,
                    allowCors: e.target.checked,
                  })
                }
                disabled={!apiSettings.enableApi}
              />
              <label
                htmlFor="allowCors"
                className={`ml-2 block text-sm ${
                  apiSettings.enableApi ? "text-gray-900" : "text-gray-400"
                }`}
              >
                Allow Cross-Origin Resource Sharing (CORS)
              </label>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" icon={Save} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
